import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Loader2,
  ChevronLeft,
  Award,
  Gift,
  RotateCcw,
  SlidersHorizontal,
  QrCode,
  Plus,
  Minus,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { api } from "../../lib/api";
import { tierPillClass, tierLabel } from "../../lib/adminTier";

const fmtNumber = (n) => Number(n || 0).toLocaleString();
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtMonthYear = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", year: "numeric" });
  } catch (_) {
    return "—";
  }
};

const initialsOf = (c) => {
  const f = (c?.first_name || "").trim();
  const l = (c?.last_name || "").trim();
  return ((f.charAt(0) + l.charAt(0)) || "?").toUpperCase();
};

const TX_FILTERS = [
  { key: "all", label: "All", testId: "tx-filter-all" },
  { key: "earn", label: "Awards", testId: "tx-filter-earn" },
  { key: "redeem", label: "Redemptions", testId: "tx-filter-redeem" },
  { key: "admin_adjustment", label: "Adjustments", testId: "tx-filter-adjust" },
  { key: "void", label: "Voids", testId: "tx-filter-void" },
];

const TxRow = ({ tx, onVoid }) => {
  const isEarn = tx.type === "earn";
  const isRedeem = tx.type === "redeem";
  const isAdjust = tx.type === "admin_adjustment";
  const isVoid = tx.type === "void";
  const voided = !!tx.voided_at;

  let Icon = Award;
  let iconClass = "bg-napoli-gold/15 text-napoli-gold";
  let verbClass = "text-napoli-gold";
  let pointsClass = "text-napoli-gold";
  let verb = "Earned";
  if (isRedeem) {
    Icon = Gift;
    iconClass = "bg-napoli-oxblood/20 text-napoli-rose";
    verbClass = "text-napoli-oxblood";
    pointsClass = "text-napoli-oxblood";
    verb = "Redeemed";
  } else if (isAdjust) {
    Icon = SlidersHorizontal;
    iconClass = "bg-napoli-rose/30 text-napoli-oxblood";
    verbClass = "text-napoli-oxblood";
    pointsClass = tx.points_delta >= 0 ? "text-napoli-gold" : "text-napoli-oxblood";
    verb = "Admin adjusted";
  } else if (isVoid) {
    Icon = RotateCcw;
    iconClass = "bg-napoli-espresso-soft/20 text-napoli-espresso-soft";
    verbClass = "text-napoli-espresso-soft";
    pointsClass = "text-napoli-espresso-soft";
    verb = "Voided";
  }
  const points = `${tx.points_delta > 0 ? "+" : ""}${tx.points_delta.toLocaleString()} pts`;
  const amount = tx.amount_spent != null ? `$${tx.amount_spent.toFixed(2)} check` : null;
  const staffName = tx.staff_first_name
    ? [tx.staff_first_name, tx.staff_last_name].filter(Boolean).join(" ").trim()
    : null;

  return (
    <li
      data-testid={`admin-tx-row-${tx.id}`}
      className={`flex items-start gap-4 px-5 py-4 border-b border-napoli-gold/15 last:border-0 ${voided ? "opacity-50" : ""}`}
    >
      <div aria-hidden className={`flex-shrink-0 mt-0.5 w-8 h-8 rounded-full flex items-center justify-center ${iconClass}`}>
        <Icon size={15} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-body text-[14px] text-napoli-espresso leading-snug">
          <span className={`font-medium ${verbClass}`}>{verb}</span>{" "}
          {isRedeem ? (
            <>
              <span className="font-medium italic text-napoli-espresso/85">
                {tx.reward_name || "a reward"}
              </span>{" "}
              <span className={`font-bold ${pointsClass}`}>{points}</span>
            </>
          ) : (
            <>
              <span className={`font-bold ${pointsClass}`}>{points}</span>
              {amount ? <span className="font-medium text-[13px] text-napoli-espresso-soft"> · {amount}</span> : null}
            </>
          )}
          {staffName && !isAdjust ? (
            <span className="font-medium text-[12px] text-napoli-espresso-soft"> · by {staffName}</span>
          ) : null}
          {voided ? (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 bg-napoli-espresso-soft/20 text-napoli-espresso-soft border border-napoli-espresso-soft/40 font-body font-semibold text-[10px] tracking-[0.18em] uppercase">
              Voided
            </span>
          ) : null}
        </p>
        {tx.notes ? (
          <p className="mt-1 font-body italic text-[12px] text-napoli-espresso-soft">"{tx.notes}"</p>
        ) : null}
        <p className="mt-1.5 font-body font-medium text-[11px] tracking-[0.1em] uppercase text-napoli-espresso-soft/80">
          {tx.created_at_relative}
        </p>
      </div>
      {!voided && !isVoid ? (
        <button
          type="button"
          data-testid={`admin-tx-void-${tx.id}`}
          onClick={() => onVoid(tx)}
          className="self-start font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-oxblood hover:text-napoli-oxblood-dark border border-napoli-oxblood/40 hover:border-napoli-oxblood px-3 py-1.5 transition-colors"
        >
          Void
        </button>
      ) : null}
    </li>
  );
};

// ----- Adjust points modal -----
const AdjustPointsModal = ({ open, onClose, customer, onSuccess }) => {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sign, setSign] = useState(1); // +1 or -1
  const [notify, setNotify] = useState(true);
  const [notifyManual, setNotifyManual] = useState(false);

  useEffect(() => {
    if (!open) {
      setAmount("");
      setReason("");
      setSign(1);
      setNotify(true);
      setNotifyManual(false);
      setSubmitting(false);
    }
  }, [open]);

  // Smart default: positive sign → notify ON; negative sign → notify OFF.
  // Only auto-flip when the admin hasn't manually overridden.
  useEffect(() => {
    if (!notifyManual) {
      setNotify(sign > 0);
    }
  }, [sign, notifyManual]);

  const parsed = parseInt(amount || "0", 10) || 0;
  const delta = sign * parsed;
  const newBalance = (customer?.points_balance || 0) + delta;
  const reasonValid = reason.trim().length >= 3;
  const canSubmit = !submitting && parsed > 0 && reasonValid;

  const submit = async () => {
    if (!canSubmit || !customer) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/admin/customer/${customer.id}/adjust-points`, {
        points_delta: delta,
        reason: reason.trim(),
        notify_customer: notify,
      });
      const label = delta > 0 ? "added" : "removed";
      const emailNote = notify ? " · email sent" : "";
      toast.success(
        `${Math.abs(delta).toLocaleString()} pts ${label}. ${customer.first_name || "Customer"}'s new balance: ${data.new_balance.toLocaleString()} pts.${emailNote}`,
      );
      onSuccess();
      onClose();
    } catch (e) {
      toast.error("Couldn't adjust points. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="adjust-points-modal"
        className="!bg-napoli-marble !border-napoli-gold/30 !rounded-none !text-napoli-espresso !max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-[24px] text-napoli-espresso text-left">
            Adjust points
          </DialogTitle>
          <DialogDescription className="font-body text-[13px] text-napoli-espresso-soft text-left">
            For {customer ? [customer.first_name, customer.last_name].filter(Boolean).join(" ") : "—"}. Manual changes are audited as `admin_adjustment` transactions.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="block font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft mb-2">
              Direction
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="adjust-sign-add"
                onClick={() => setSign(1)}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 font-body font-semibold text-[12px] tracking-[0.18em] uppercase border ${
                  sign === 1
                    ? "bg-napoli-gold/20 border-napoli-gold text-napoli-oxblood"
                    : "bg-napoli-cream-warm border-napoli-gold/40 text-napoli-espresso-soft hover:border-napoli-oxblood/40"
                }`}
              >
                <Plus size={14} /> Add
              </button>
              <button
                type="button"
                data-testid="adjust-sign-remove"
                onClick={() => setSign(-1)}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 font-body font-semibold text-[12px] tracking-[0.18em] uppercase border ${
                  sign === -1
                    ? "bg-napoli-oxblood text-napoli-cream border-napoli-oxblood"
                    : "bg-napoli-cream-warm border-napoli-gold/40 text-napoli-espresso-soft hover:border-napoli-oxblood/40"
                }`}
              >
                <Minus size={14} /> Remove
              </button>
            </div>
          </div>

          <label className="block">
            <span className="block font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft mb-2">
              Points
            </span>
            <input
              data-testid="adjust-amount-input"
              type="text"
              inputMode="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
              placeholder="50"
              className="w-full bg-napoli-cream-warm border border-napoli-gold/40 px-3 py-2.5 font-body text-[16px] text-napoli-espresso focus:outline-none focus:border-napoli-oxblood transition-colors"
            />
          </label>

          <label className="block">
            <span className="block font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft mb-2">
              Reason · required for audit
            </span>
            <textarea
              data-testid="adjust-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 200))}
              placeholder="Birthday bonus, accidental scan, comp dessert…"
              rows={2}
              className="w-full bg-napoli-cream-warm border border-napoli-gold/40 px-3 py-2.5 font-body text-[13px] text-napoli-espresso focus:outline-none focus:border-napoli-oxblood transition-colors"
            />
          </label>

          <div className="bg-napoli-cream-warm/70 border-l-2 border-napoli-oxblood px-3 py-3">
            <p className="font-body font-semibold text-[10px] tracking-[0.3em] uppercase text-napoli-oxblood">
              Preview
            </p>
            <p data-testid="adjust-preview" className="mt-1 font-display font-bold text-[20px] text-napoli-oxblood leading-none">
              New balance: {fmtNumber(newBalance)} pts
            </p>
            <p className="mt-1 font-body italic text-[11px] text-napoli-espresso-soft">
              {parsed > 0 ? `${delta > 0 ? "+" : ""}${delta.toLocaleString()} from current ${fmtNumber(customer?.points_balance || 0)} pts` : "Enter an amount."}
            </p>
          </div>

          <label
            htmlFor="adjust-notify-checkbox"
            data-testid="adjust-notify-row"
            className="flex items-start gap-3 cursor-pointer bg-napoli-cream-warm/60 border border-napoli-gold/25 px-3 py-3"
          >
            <input
              id="adjust-notify-checkbox"
              data-testid="adjust-notify-checkbox"
              type="checkbox"
              checked={notify}
              onChange={(e) => {
                setNotifyManual(true);
                setNotify(e.target.checked);
              }}
              className="mt-0.5 h-4 w-4 accent-napoli-oxblood"
            />
            <span className="flex-1">
              <span className="block font-body text-[13px] text-napoli-espresso">
                Notify customer by email
              </span>
              <span
                data-testid="adjust-notify-sublabel"
                className="block mt-1 font-body italic text-[11px] text-napoli-espresso-soft"
              >
                {!notify
                  ? "No email will be sent."
                  : delta > 0
                  ? "We'll send a friendly email about the bonus."
                  : delta < 0
                  ? "We'll send a brief note explaining the adjustment."
                  : "Pick an amount to preview the email tone."}
              </span>
            </span>
          </label>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              data-testid="adjust-cancel"
              onClick={onClose}
              className="flex-1 border border-napoli-espresso/40 text-napoli-espresso font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-3 hover:bg-napoli-cream-warm transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="adjust-submit"
              disabled={!canSubmit}
              onClick={submit}
              className="flex-1 bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-3 hover:bg-napoli-oxblood-dark transition-colors disabled:opacity-50"
            >
              {submitting ? "…" : "Adjust"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ----- Void modal -----
const VoidModal = ({ open, onClose, tx, customerName, onSuccess }) => {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [notify, setNotify] = useState(true);

  useEffect(() => {
    if (!open) {
      setReason("");
      setSubmitting(false);
      setNotify(true);
    }
  }, [open]);

  const reasonValid = reason.trim().length >= 3;
  const canSubmit = !submitting && reasonValid && !!tx;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/admin/transaction/${tx.id}/void`, {
        reason: reason.trim(),
        notify_customer: notify,
      });
      const emailNote = notify ? " · email sent" : "";
      toast.success(`Transaction voided. ${customerName}'s new balance: ${data.customer_new_balance.toLocaleString()} pts.${emailNote}`);
      onSuccess();
      onClose();
    } catch (e) {
      const code = e?.response?.data?.detail?.error;
      toast.error(
        code === "already_voided" ? "This transaction is already voided." :
        code === "cannot_void_void" ? "Void transactions can't themselves be voided." :
        "Couldn't void this transaction. Try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const desc = tx
    ? tx.type === "earn"
      ? `Awarded ${tx.points_delta} pts${tx.amount_spent != null ? ` · ${fmtMoney(tx.amount_spent)} check` : ""}`
      : tx.type === "redeem"
        ? `Redeemed ${tx.reward_name || "a reward"} for ${Math.abs(tx.points_delta).toLocaleString()} pts`
        : `Admin ${tx.points_delta >= 0 ? "added" : "removed"} ${Math.abs(tx.points_delta).toLocaleString()} pts`
    : "";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="void-modal"
        className="!bg-napoli-marble !border-napoli-gold/30 !rounded-none !text-napoli-espresso !max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-[24px] text-napoli-espresso text-left">
            Void transaction
          </DialogTitle>
          <DialogDescription className="font-body text-[13px] text-napoli-espresso-soft text-left">
            {desc} · Reversal will be logged as an audit record.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <label className="block">
            <span className="block font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft mb-2">
              Reason · required
            </span>
            <textarea
              data-testid="void-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 200))}
              placeholder="Accidental scan, customer disputed, training error…"
              rows={3}
              className="w-full bg-napoli-cream-warm border border-napoli-gold/40 px-3 py-2.5 font-body text-[13px] text-napoli-espresso focus:outline-none focus:border-napoli-oxblood transition-colors"
            />
          </label>

          <label
            htmlFor="void-notify-checkbox"
            data-testid="void-notify-row"
            className="flex items-start gap-3 cursor-pointer bg-napoli-cream-warm/60 border border-napoli-gold/25 px-3 py-3"
          >
            <input
              id="void-notify-checkbox"
              data-testid="void-notify-checkbox"
              type="checkbox"
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-napoli-oxblood"
            />
            <span className="flex-1">
              <span className="block font-body text-[13px] text-napoli-espresso">
                Notify customer by email
              </span>
              <span
                data-testid="void-notify-sublabel"
                className="block mt-1 font-body italic text-[11px] text-napoli-espresso-soft"
              >
                {notify
                  ? "We'll send a brief note about the balance update."
                  : "No email will be sent."}
              </span>
            </span>
          </label>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              data-testid="void-cancel"
              onClick={onClose}
              className="flex-1 border border-napoli-espresso/40 text-napoli-espresso font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-3 hover:bg-napoli-cream-warm transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="void-submit"
              disabled={!canSubmit}
              onClick={submit}
              className="flex-1 bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-3 hover:bg-napoli-oxblood-dark transition-colors disabled:opacity-50"
            >
              {submitting ? "…" : "Void"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ----- QR modal -----
const QrModal = ({ open, onClose, customer }) => {
  const url = customer ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(customer.qr_payload || `napoli:cust:${customer.id}`)}` : null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="qr-modal"
        className="!bg-napoli-marble !border-napoli-gold/30 !rounded-none !text-napoli-espresso !max-w-sm"
      >
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-[24px] text-napoli-espresso text-left">
            {customer?.first_name || "Member"}'s QR
          </DialogTitle>
          <DialogDescription className="font-body text-[13px] text-napoli-espresso-soft text-left">
            Code {customer?.customer_code || "—"} · for verification when staff scans.
          </DialogDescription>
        </DialogHeader>
        <div className="bg-napoli-cream-warm border border-napoli-gold/40 p-4 flex items-center justify-center">
          {url ? <img src={url} alt="QR" className="w-64 h-64" data-testid="qr-modal-image" /> : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AdminCustomerDetailPage = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [voidTx, setVoidTx] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [filter, setFilter] = useState("all");

  const reload = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get(`/admin/customer/${customerId}`);
      setData(data);
    } catch (e) {
      const code = e?.response?.data?.detail?.error;
      setErr(code === "customer_not_found" ? "Customer not found." : "Couldn't load customer.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const filteredTx = useMemo(() => {
    if (!data) return [];
    if (filter === "all") return data.transactions;
    return data.transactions.filter((t) => t.type === filter);
  }, [data, filter]);

  if (loading) {
    return (
      <section className="max-w-5xl mx-auto px-6 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-napoli-marble" />
          <div className="h-40 bg-napoli-marble" />
          <div className="h-32 bg-napoli-marble" />
        </div>
      </section>
    );
  }

  if (err || !data) {
    return (
      <section className="max-w-5xl mx-auto px-6 py-12 text-center">
        <p data-testid="admin-customer-error" className="font-display font-bold text-[20px] text-napoli-espresso">
          {err || "Customer unavailable."}
        </p>
        <Link
          to="/admin/customers"
          data-testid="back-to-customers"
          className="mt-6 inline-flex items-center gap-1 font-body font-medium text-[12px] tracking-[0.05em] text-napoli-oxblood"
        >
          <ChevronLeft size={14} /> Back to customers
        </Link>
      </section>
    );
  }

  const c = data.customer;
  const fullName = [c.first_name, c.last_name].filter(Boolean).join(" ") || "Member";
  const comp = data.computed;

  return (
    <section data-testid="admin-customer-detail" className="max-w-5xl mx-auto px-6 py-8">
      {/* Breadcrumb */}
      <nav className="font-body text-[12px] text-napoli-espresso-soft" aria-label="Breadcrumb">
        <Link to="/admin/customers" className="hover:text-napoli-oxblood transition-colors">
          ← Customers
        </Link>{" "}
        / <span className="text-napoli-oxblood font-medium">{fullName}</span>
      </nav>

      {/* Hero card */}
      <div data-testid="customer-hero" className="mt-4 bg-napoli-marble border border-napoli-gold/20 p-6">
        <div className="flex items-start gap-5">
          <div
            aria-hidden
            className="h-20 w-20 rounded-full bg-napoli-cream-warm border-2 border-napoli-gold text-napoli-oxblood flex items-center justify-center font-display font-bold text-[26px] flex-shrink-0"
          >
            {initialsOf(c)}
          </div>
          <div className="flex-1 min-w-0">
            <h1 data-testid="customer-name" className="font-display font-bold text-[28px] text-napoli-espresso leading-tight">
              {fullName}
            </h1>
            <p className="mt-1 font-body italic text-[13px] text-napoli-espresso-soft">
              Member since {fmtMonthYear(c.created_at)} · code <span className="font-mono not-italic">{c.customer_code}</span>
            </p>
            <div className="mt-3 flex flex-wrap gap-2 items-center">
              <span className={tierPillClass(c.tier)} data-testid="customer-tier">{tierLabel(c.tier)}</span>
              <span
                data-testid="customer-balance"
                className="inline-flex items-center px-3 py-1 border border-napoli-gold/40 bg-napoli-cream-warm font-body font-bold text-[12px] tracking-[0.18em] uppercase text-napoli-gold"
              >
                {fmtNumber(c.points_balance)} pts
              </span>
            </div>
            <p className="mt-3 font-body text-[12px] text-napoli-espresso-soft">
              Lifetime <span className="font-semibold text-napoli-espresso">{fmtNumber(c.lifetime_points)} pts</span>{" "}
              · {comp.total_lifetime_visits} visits · {comp.total_lifetime_redemptions} redemptions · {fmtMoney(comp.total_lifetime_check_revenue)} check revenue
            </p>
          </div>
        </div>
      </div>

      {/* Action bar */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          data-testid="action-adjust-points"
          onClick={() => setAdjustOpen(true)}
          className="inline-flex items-center justify-center gap-2 bg-napoli-oxblood text-napoli-cream py-4 font-body font-semibold text-[12px] tracking-[0.22em] uppercase hover:bg-napoli-oxblood-dark transition-colors"
        >
          <SlidersHorizontal size={16} /> Adjust points
        </button>
        <button
          type="button"
          data-testid="action-view-qr"
          onClick={() => setQrOpen(true)}
          className="inline-flex items-center justify-center gap-2 bg-napoli-cream-warm border border-napoli-gold/40 text-napoli-espresso py-4 font-body font-semibold text-[12px] tracking-[0.22em] uppercase hover:border-napoli-oxblood/40 transition-colors"
        >
          <QrCode size={16} /> View QR
        </button>
      </div>

      {/* Transactions */}
      <div className="mt-8">
        <p className="font-body font-semibold text-[10px] tracking-[0.3em] uppercase text-napoli-oxblood">
          All activity
        </p>
        <h2 className="mt-2 font-display font-bold text-[24px] text-napoli-espresso">
          {c.first_name || "Member"}'s full history.
        </h2>

        {/* Filter pills */}
        <div className="mt-4 flex flex-wrap gap-2" role="tablist" aria-label="Filter transactions">
          {TX_FILTERS.map((f) => {
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={f.testId}
                onClick={() => setFilter(f.key)}
                className={[
                  "px-3 py-1.5 font-body font-medium text-[12px] tracking-[0.1em] uppercase border transition-colors",
                  active
                    ? "bg-napoli-oxblood text-napoli-cream border-napoli-oxblood"
                    : "bg-napoli-cream-warm text-napoli-espresso border-napoli-gold/40 hover:border-napoli-oxblood/40",
                ].join(" ")}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        <ul
          data-testid="admin-tx-list"
          className="mt-4 bg-napoli-marble border border-napoli-gold/20"
        >
          {filteredTx.length === 0 ? (
            <li className="px-5 py-10 text-center font-body italic text-[13px] text-napoli-espresso-soft">
              No matching transactions.
            </li>
          ) : (
            filteredTx.map((tx) => <TxRow key={tx.id} tx={tx} onVoid={setVoidTx} />)
          )}
        </ul>
      </div>

      <AdjustPointsModal
        open={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        customer={c}
        onSuccess={reload}
      />
      <VoidModal
        open={!!voidTx}
        onClose={() => setVoidTx(null)}
        tx={voidTx}
        customerName={fullName}
        onSuccess={reload}
      />
      <QrModal open={qrOpen} onClose={() => setQrOpen(false)} customer={c} />
    </section>
  );
};

export default AdminCustomerDetailPage;
